import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import * as mapboxgl from "mapbox-gl";
import { MapBoxService } from "../../_services/mapbox.service";
import { ListsSimpleChange } from "../../_components/planning-assistant/map/map.component";
import { PAUtil } from "../../_components/planning-assistant/classes/util";
import { TourAnimation } from "../../_components/planning-assistant/classes/tour-animation";
import { MapboxFeatureInstance, MapDisplayUnit } from "../../_models/map-display-unit.model";

export interface PointGeometry {
  type: "Point";
  coordinates: [number, number];
}

export interface FeatureProperties {
  id: number;
  short_name: string;
  bg_color: string;
  stroke_color: string;
  font_color: string;
  feature_instance: MapboxFeatureInstance,
  ticket_count: number
}

@Component({
  selector: 'hawk-dashboard-map',
  imports: [],
  templateUrl: './dashboard-map.component.html',
  standalone: true,
  styleUrl: './dashboard-map.component.scss'
})
export class DashboardMapComponent implements AfterViewInit, OnChanges {

  @Input() mapDisplayUnits: MapDisplayUnit<any[], any[]>[] = []
  @Input() navigation: {center?: {lat: number, lng: number}, zoom?: number} = {center: { lat: 51.4, lng: 10.4476 }, zoom: 5}

  @ViewChild('MapSizeObsContainer') mapSizeObsContainer: ElementRef<HTMLDivElement>;
  private map: mapboxgl.Map;
  private runningChange: boolean = false;
  private changesQueue: SimpleChanges[] = []

  constructor(
    private mapBoxService: MapBoxService,
  ) { }

  ngAfterViewInit(): void {
    this.waitForMapWindow(200).then(
      _ => {
        this.map = new mapboxgl.Map({
          accessToken: this.mapBoxService.accessToken,
          container: 'map_window',
          style: 'mapbox://styles/mapbox/streets-v9?optimize=true',
          center: this.navigation?.center || { lat: 51.4, lng: 10.4476 },
          zoom: this.navigation?.zoom || 5
        })
      }
    )
    const map_size_observer = new ResizeObserver(entries => {
      entries.forEach(_ => {
        if (this.map) {
          this.map.resize()
        }
      });
    });
    map_size_observer.observe(this.mapSizeObsContainer.nativeElement);
  }

  ngOnChanges(changes: SimpleChanges) {
    this.changesQueue.push(this.cloneChanges(changes))
    this.waitForMapAndProcessChanges()
  }

  async processChanges(changes: SimpleChanges): Promise<void> {
    if (changes.mapDisplayUnits) {
      let display_units_changed = new ListsSimpleChange<MapDisplayUnit<any[], any[]>>(changes.mapDisplayUnits);
      display_units_changed.getRemovedValues().map(mdu => mdu.removeFromMap())
      display_units_changed.getAddedValues().map(mdu => mdu.displayOnMap(this.map))
    }

    if (changes.tourAnimations) {
      let tour_animations_changed = new ListsSimpleChange<TourAnimation>(changes.tourAnimations);
      tour_animations_changed.getRemovedValues().map(ta => ta.initAnimation())
      tour_animations_changed.getAddedValues().map(ta => ta.deleteCurrentLayers())
    }

    if (changes.navigation && changes.navigation.currentValue) {
      let zoom = changes.navigation.currentValue.zoom
      let center = changes.navigation.currentValue.center
      if (center) {
        this.map.flyTo({center: changes.navigation.currentValue.center, duration: 400, zoom})
      } else if (zoom) {
        this.map.setZoom(zoom)
      }
    }
  }

  async waitForMapAndProcessChanges() {
    if (!this.runningChange) {
      this.runningChange = true
      await this.waitForMapStyle()
      while (this.changesQueue.length) {
        await this.processChanges(this.changesQueue.splice(0, 1)[0])
      }
      this.runningChange = false
    }
  }

  async waitForMapStyle() {
    let done = false
    while (!done) {
      await PAUtil.sleep(50)
      try {
        if (this.map) {
          this.map.getStyle()
          done = true
        }
      } catch (err) {
        console.log(err)
        await this.waitForMapStyle()
        done = true
      }
    }
  }

  cloneChanges(changes: SimpleChanges): SimpleChanges {
    // cloning changes to prevent first changes object values being overridden with later changes object values by angular source code for some reason
    return  {
      ...changes,
    }
  }

  private async waitForMapWindow(tries: number) {
    while (!document.getElementById('map_window') && tries > 0) {
      console.log('Trying to get map: attempt ' + tries)
      await PAUtil.sleep(100)
      if (tries) tries -= 1
    }
  }
}


